import React, { useState, useEffect, useMemo } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Box,
   Button,
   Flex,
   FormControl,
   FormLabel,
   Heading,
   Input,
   Select,
   Tab,
   TabList,
   TabPanel,
   TabPanels,
   Tabs,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Badge,
   Spinner,
   Text,
   Checkbox,
   useColorMode,
   useColorModeValue,
   AlertDialog,
   AlertDialogBody,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogContent,
   AlertDialogOverlay,
   useDisclosure,
   useBreakpointValue,
   Drawer,
   DrawerBody,
   DrawerFooter,
   DrawerHeader,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   IconButton,
   Collapse,
   VStack,
   HStack,
   SimpleGrid,
   Card,
   CardBody,
   CardHeader,
   Stack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getEwayBillandEinvoice, generateBulkEwayEinvoice } from '../../../../redux/actions/utility';
import { HamburgerIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const DOCUMENT_CONFIG = {
   'e-way-bill': {
      title: 'E-WAY BILL',
      operationType: 'eway',
      tabs: [{ label: 'E-Way Bills', type: 'sales' }],
      columns: {
         main: [
            { key: 'daybookName', label: 'Daybook', type: 'text', priority: 'high' },
            { key: 'docno', label: 'Doc Number', type: 'text', alt: 'docNumber', weight: 'medium', priority: 'high' },
            { key: 'transactionAt', label: 'Date', type: 'date', priority: 'medium' },
            { key: 'gstRegNo', label: 'GSTIN', type: 'mono', priority: 'low' },
            { key: 'accountName', label: 'Account', type: 'text', priority: 'high' },
            { key: 'netAmount', label: 'Net Amount', type: 'currency', numeric: true, priority: 'medium' },
            { key: 'taxableAmount', label: 'Taxable Value', type: 'currency', numeric: true, priority: 'low' },
            { key: 'gstAmount', label: 'GST Amount', type: 'currency', numeric: true, priority: 'low' },
            { key: 'trVehicleNo', label: 'Vehicle No', type: 'text', priority: 'low' },
            { key: 'trlRNo', label: 'LR No', type: 'text', priority: 'low' },
            { key: 'trLRDate', label: 'LR Date', type: 'date', priority: 'low' },
            { key: 'transportName', label: 'Transport', type: 'text', priority: 'low' },
            { key: 'transportGSTIN', label: 'Transport GSTIN', type: 'mono', priority: 'low' },
            { key: 'city', label: 'City', type: 'text', priority: 'medium' },
            { key: 'eWayBillStatus', label: 'Status', type: 'eWayBillStatus', priority: 'high' },
         ],
      },
   },
   'e-invoice': {
      title: 'E-INVOICE',
      operationType: 'einvoice',
      tabs: [
         { label: 'Sales', type: 'sales' },
         { label: 'SalesReturn', type: 'salesReturn' },
      ],
      columns: {
         main: [
            { key: 'daybookName', label: 'Daybook', type: 'text', priority: 'high' },
            { key: 'docno', label: 'Doc Number', type: 'text', alt: 'docNumber', weight: 'medium', priority: 'high' },
            { key: 'transactionAt', label: 'Date', type: 'date', priority: 'medium' },
            { key: 'gstRegNo', label: 'GSTIN', type: 'mono', priority: 'low' },
            { key: 'accountName', label: 'Account', type: 'text', priority: 'high' },
            { key: 'netAmount', label: 'Net Amount', type: 'currency', numeric: true, priority: 'medium' },
            { key: 'taxableAmount', label: 'Taxable Value', type: 'currency', numeric: true, priority: 'low' },
            { key: 'gstAmount', label: 'GST Amount', type: 'currency', numeric: true, priority: 'low' },
            { key: 'trVehicleNo', label: 'Vehicle No', type: 'text', priority: 'low' },
            { key: 'trlRNo', label: 'LR No', type: 'text', priority: 'low' },
            { key: 'trLRDate', label: 'LR Date', type: 'date', priority: 'low' },
            { key: 'transportName', label: 'Transport', type: 'text', priority: 'low' },
            { key: 'transportGSTIN', label: 'Transport GSTIN', type: 'mono', priority: 'low' },
            { key: 'city', label: 'City', type: 'text', priority: 'medium' },
            { key: 'einvoiceirn', label: 'IRN', type: 'mono', priority: 'low' },
            { key: 'einvoiceackno', label: 'Ack No', type: 'text', priority: 'low' },
            { key: 'einvoiceackdate', label: 'Ack Date', type: 'date', priority: 'low' },
            { key: 'eInvoiceStatus', label: 'Status', type: 'eInvoiceStatus', priority: 'high' },
         ],
      },
   },
};

const STATUS_OPTIONS = [
   { value: 'all', label: 'All' },
   { value: 'pending', label: 'Pending' },
   { value: 'generated', label: 'Generated' },
   { value: 'cancelled', label: 'Cancelled' },
];

const MobileCard = ({ item, columns, textColor, activeDocumentType, colorMode, onSelectItem }) => {
   const [isExpanded, setIsExpanded] = useState(false);

   const formatDateForDisplay = (dateString) => {
      if (!dateString) return '-';
      try {
         const date = new Date(dateString);
         return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
      } catch {
         return '-';
      }
   };

   const getStatusColor = (status) => {
      const statusLower = status?.toString()?.toLowerCase() || '';
      const colors = { pending: 'yellow', generated: 'green', failed: 'red', cancelled: 'gray' };
      return colors[statusLower] || 'gray';
   };

   const renderValue = (column, value) => {
      if (!value || value === '') return '-';

      switch (column.type) {
         case 'date':
            return formatDateForDisplay(value);
         case 'currency':
            return `₹${Number(value).toLocaleString()}`;
         case 'distance':
            return `${value} km`;
         case 'status':
         case 'eWayBillStatus':
         case 'eInvoiceStatus':
            return (
               <Badge colorScheme={getStatusColor(value)} size="sm">
                  {value?.toString()?.toUpperCase() || 'N/A'}
               </Badge>
            );
         case 'badge':
            return (
               <Badge colorScheme={value === 'sales' ? 'green' : 'blue'} size="sm">
                  {value.toUpperCase()}
               </Badge>
            );
         case 'error':
            return (
               <Text color="red.500" fontSize="sm">
                  {value}
               </Text>
            );
         default:
            return value;
      }
   };

   const highPriorityColumns = columns.filter((col) => col.priority === 'high');
   const mediumPriorityColumns = columns.filter((col) => col.priority === 'medium');
   const lowPriorityColumns = columns.filter((col) => col.priority === 'low');

   const getRowBackgroundColor = () => {
      const statusField = activeDocumentType === 'e-way-bill' ? 'eWayBillStatus' : 'eInvoiceStatus';
      const status = item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';

      const colors = {
         light: {
            generated: 'green.50',
            pending: 'transparent',
            cancelled: 'red.50',
            failed: 'red.50',
         },
         dark: {
            generated: 'green.900',
            pending: 'transparent',
            cancelled: 'red.900',
            failed: 'red.900',
         },
      };

      const modeColors = colors[colorMode] || colors.light;
      return modeColors[status] || 'transparent';
   };

   return (
      <Card mb={3} bg={getRowBackgroundColor()} shadow="sm">
         <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
               <HStack spacing={2}>
                  <Checkbox
                     isChecked={item.selected}
                     onChange={(e) => onSelectItem(item.id, e.target.checked)}
                     colorScheme="blue"
                  />
                  <Text fontWeight="medium" fontSize="sm" color={textColor}>
                     {item.docno || item.docNumber || 'N/A'}
                  </Text>
               </HStack>
               <IconButton
                  icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsExpanded(!isExpanded)}
               />
            </Flex>
         </CardHeader>

         <CardBody pt={0}>
            <VStack align="stretch" spacing={2}>
               {highPriorityColumns.map((column) => {
                  const value = item[column.key] || item[column.alt] || '';
                  return (
                     <Flex key={column.key} justify="space-between" align="center">
                        <Text fontSize="xs" color="gray.500" minW="80px">
                           {column.label}:
                        </Text>
                        <Text fontSize="sm" color={textColor} textAlign="right">
                           {renderValue(column, value)}
                        </Text>
                     </Flex>
                  );
               })}
            </VStack>

            <Collapse in={isExpanded} animateOpacity>
               <VStack align="stretch" spacing={2} mt={3} pt={3} borderTop="1px" borderColor="gray.200">
                  {[...mediumPriorityColumns, ...lowPriorityColumns].map((column) => {
                     const value = item[column.key] || item[column.alt] || '';
                     return (
                        <Flex key={column.key} justify="space-between" align="center">
                           <Text fontSize="xs" color="gray.500" minW="80px">
                              {column.label}:
                           </Text>
                           <Text fontSize="sm" color={textColor} textAlign="right">
                              {renderValue(column, value)}
                           </Text>
                        </Flex>
                     );
                  })}
               </VStack>
            </Collapse>
         </CardBody>
      </Card>
   );
};

const TableCell = ({ column, data, textColor }) => {
   const value = data[column.key] || data[column.alt] || '';

   const formatDateForDisplay = (dateString) => {
      if (!dateString) return '-';
      try {
         const date = new Date(dateString);
         return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
      } catch {
         return '-';
      }
   };

   const getStatusColor = (status) => {
      const statusLower = status?.toString()?.toLowerCase() || '';
      const colors = { pending: 'yellow', generated: 'green', failed: 'red', cancelled: 'gray' };
      return colors[statusLower] || 'gray';
   };

   const renderContent = () => {
      if (!value || value === '') return '-';

      switch (column.type) {
         case 'date':
            return formatDateForDisplay(value);
         case 'currency':
            return `₹${Number(value).toLocaleString()}`;
         case 'distance':
            return `${value} km`;
         case 'status':
         case 'eWayBillStatus':
         case 'eInvoiceStatus':
            return (
               <Badge colorScheme={getStatusColor(value)} size="sm">
                  {value?.toString()?.toUpperCase() || 'N/A'}
               </Badge>
            );
         case 'badge':
            return (
               <Badge colorScheme={value === 'sales' ? 'green' : 'blue'} size="sm">
                  {value.toUpperCase()}
               </Badge>
            );
         case 'error':
            return (
               <Text color="red.500" fontSize="sm">
                  {value}
               </Text>
            );
         default:
            return value;
      }
   };

   const getStyles = () => {
      const styles = { fontSize: 'sm', color: textColor };

      if (column.type === 'mono') {
         styles.fontSize = 'xs';
         styles.fontFamily = 'mono';
      }

      if (column.weight) {
         styles.fontWeight = column.weight;
      }

      return styles;
   };

   return (
      <Td isNumeric={column.numeric} {...getStyles()}>
         {renderContent()}
      </Td>
   );
};

const getRowBackgroundColor = (item, activeDocumentType, colorMode) => {
   const statusField = activeDocumentType === 'e-way-bill' ? 'eWayBillStatus' : 'eInvoiceStatus';
   const status = item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';

   const colors = {
      light: {
         generated: 'green.50',
         pending: 'transparent',
         cancelled: 'red.50',
         failed: 'red.50',
      },
      dark: {
         generated: 'green.900',
         pending: 'transparent',
         cancelled: 'red.900',
         failed: 'red.900',
      },
   };

   const modeColors = colors[colorMode] || colors.light;
   return modeColors[status] || 'transparent';
};

const getRowTextColor = (item, activeDocumentType, textColor) => {
   const statusField = activeDocumentType === 'e-way-bill' ? 'eWayBillStatus' : 'eInvoiceStatus';
   const status = item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';

   switch (status) {
      case 'generated':
         return 'green.600';
      case 'pending':
         return textColor;
      case 'cancelled':
      case 'failed':
         return 'red.600';
      default:
         return textColor;
   }
};

const ResponsiveDataDisplay = ({
   columns,
   data,
   loading,
   textColor,
   colorMode,
   tableHeaderBg,
   tableHoverBg,
   selectAll,
   onSelectAll,
   onSelectItem,
   activeDocumentType,
   isMobile,
}) => {
   if (isMobile) {
      return (
         <Box p={4} maxH="400px" overflowY="auto">
            {loading ? (
               <Flex justify="center" align="center" py={8} direction="column">
                  <Spinner size="lg" />
                  <Text mt={2} color={textColor}>
                     Loading data...
                  </Text>
               </Flex>
            ) : data.length === 0 ? (
               <Flex justify="center" align="center" py={8}>
                  <Text color={textColor}>No records found</Text>
               </Flex>
            ) : (
               <>
                  <Flex justify="space-between" align="center" mb={4} p={2} bg={tableHeaderBg} borderRadius="md">
                     <HStack>
                        <Checkbox
                           isChecked={selectAll}
                           onChange={(e) => onSelectAll(e.target.checked)}
                           colorScheme="blue"
                        />
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                           Select All ({data.length})
                        </Text>
                     </HStack>
                  </Flex>
                  {data.map((item) => (
                     <MobileCard
                        key={item.id}
                        item={item}
                        columns={columns}
                        textColor={textColor}
                        activeDocumentType={activeDocumentType}
                        colorMode={colorMode}
                        onSelectItem={onSelectItem}
                     />
                  ))}
               </>
            )}
         </Box>
      );
   }

   const visibleColumns = columns;

   return (
      <Box overflowX="auto" maxH="400px" overflowY="auto">
         <Table size="sm" variant="simple" colorScheme={colorMode}>
            <Thead bg={tableHeaderBg} position="sticky" top={0} zIndex={1}>
               <Tr>
                  <Th p={2} color={textColor}>
                     <Checkbox
                        isChecked={selectAll}
                        onChange={(e) => onSelectAll(e.target.checked)}
                        colorScheme="blue"
                     />
                  </Th>
                  {visibleColumns.map((column, index) => (
                     <Th key={index} isNumeric={column.numeric} color={textColor} fontSize="xs">
                        {column.label}
                     </Th>
                  ))}
               </Tr>
            </Thead>
            <Tbody>
               {loading ? (
                  <Tr>
                     <Td colSpan={visibleColumns.length + 1} textAlign="center" py={8}>
                        <Spinner size="lg" />
                        <Text mt={2} color={textColor}>
                           Loading data...
                        </Text>
                     </Td>
                  </Tr>
               ) : data.length === 0 ? (
                  <Tr>
                     <Td colSpan={visibleColumns.length + 1} textAlign="center" py={8}>
                        <Text color={textColor}>No records found</Text>
                     </Td>
                  </Tr>
               ) : (
                  data.map((item) => {
                     const rowBgColor = getRowBackgroundColor(item, activeDocumentType, colorMode);
                     const rowTextColor = getRowTextColor(item, activeDocumentType, textColor);

                     return (
                        <Tr
                           key={item.id}
                           bg={rowBgColor}
                           color={rowTextColor}
                           _hover={{
                              bg: rowBgColor !== 'transparent' ? rowBgColor : tableHoverBg,
                           }}>
                           <Td p={2}>
                              <Checkbox
                                 isChecked={item.selected}
                                 onChange={(e) => onSelectItem(item.id, e.target.checked)}
                                 colorScheme="blue"
                              />
                           </Td>
                           {visibleColumns.map((column, index) => (
                              <TableCell key={index} column={column} data={item} textColor={rowTextColor} />
                           ))}
                        </Tr>
                     );
                  })
               )}
            </Tbody>
         </Table>
      </Box>
   );
};

const MobileFilterPanel = ({
   isOpen,
   onClose,
   fromDate,
   setFromDate,
   toDate,
   setToDate,
   activeDocumentType,
   handleDocumentTypeChange,
   statusFilter,
   setStatusFilter,
   textColor,
   inputBg,
}) => {
   return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} size="sm">
         <DrawerOverlay />
         <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Filters & Criteria</DrawerHeader>

            <DrawerBody>
               <VStack spacing={4} align="stretch">
                  <Heading size="xs" color={textColor}>
                     Date Range
                  </Heading>

                  <SimpleGrid columns={2} spacing={3}>
                     <FormControl>
                        <FormLabel fontSize="sm" color={textColor}>
                           From Date
                        </FormLabel>
                        <Input
                           value={fromDate}
                           onChange={(e) => setFromDate(e.target.value)}
                           bg={inputBg}
                           type="date"
                           color={textColor}
                           size="sm"
                        />
                     </FormControl>

                     <FormControl>
                        <FormLabel fontSize="sm" color={textColor}>
                           To Date
                        </FormLabel>
                        <Input
                           value={toDate}
                           onChange={(e) => setToDate(e.target.value)}
                           bg={inputBg}
                           type="date"
                           color={textColor}
                           size="sm"
                        />
                     </FormControl>
                  </SimpleGrid>

                  <Heading size="xs" color={textColor}>
                     Document Type
                  </Heading>
                  <SimpleGrid columns={1} spacing={2}>
                     {Object.keys(DOCUMENT_CONFIG).map((docType) => (
                        <Button
                           key={docType}
                           colorScheme={activeDocumentType === docType ? 'blue' : 'gray'}
                           size="sm"
                           variant={activeDocumentType === docType ? 'solid' : 'outline'}
                           onClick={() => handleDocumentTypeChange(docType)}>
                           {DOCUMENT_CONFIG[docType].title}s
                        </Button>
                     ))}
                  </SimpleGrid>

                  <Heading size="xs" color={textColor}>
                     Status Filter
                  </Heading>
                  <FormControl>
                     <Select
                        size="sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        bg={inputBg}
                        color={textColor}>
                        {STATUS_OPTIONS.map((option) => (
                           <option key={option.value} value={option.value}>
                              {option.label}
                           </option>
                        ))}
                     </Select>
                  </FormControl>
               </VStack>
            </DrawerBody>

            <DrawerFooter>
               <Button variant="outline" mr={3} onClick={onClose}>
                  Apply Filters
               </Button>
            </DrawerFooter>
         </DrawerContent>
      </Drawer>
   );
};

export default function EWayBillModal({ isEwayBill, onClose, handleEwayBill }) {
   const [activeTab, setActiveTab] = useState(0);
   const [fromDate, setFromDate] = useState('2025-04-01');
   const [toDate, setToDate] = useState('2025-06-30');
   const [typeFilter, setTypeFilter] = useState('sales');
   const [statusFilter, setStatusFilter] = useState('pending');
   const [activeDocumentType, setActiveDocumentType] = useState('e-way-bill');
   const [data, setData] = useState([]);
   const [loading, setLoading] = useState(false);
   const [selectAll, setSelectAll] = useState(false);
   const [actionLoading, setActionLoading] = useState(false);
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

   const {
      isOpen: isRegenerateDialogOpen,
      onOpen: onRegenerateDialogOpen,
      onClose: onRegenerateDialogClose,
   } = useDisclosure();
   const [pendingAction, setPendingAction] = useState(null);
   const cancelRef = React.useRef();

   const companyId = Number(localStorage.getItem('companyId'));
   const billData = useSelector((state) => state?.utility?.ewayBillAndEinvoice || []);
   const dispatch = useDispatch();

   const isMobile = useBreakpointValue({ base: true, md: false });
   const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
   const modalSize = useBreakpointValue({ base: 'full', md: '4xl', lg: '6xl' });

   const bgColor = useColorModeValue('white', 'gray.800');
   const sidebarBg = useColorModeValue('gray.50', 'gray.700');
   const tableHeaderBg = useColorModeValue('gray.100', 'gray.600');
   const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const textColor = useColorModeValue('gray.700', 'gray.200');
   const footerBg = useColorModeValue('gray.50', 'gray.700');
   const inputBg = useColorModeValue('white', 'gray.600');

   const currentConfig = DOCUMENT_CONFIG[activeDocumentType];

   const handleTabChange = (tabIndex) => {
      setActiveTab(tabIndex);
      setTypeFilter(currentConfig.tabs[tabIndex].type);
      setSelectAll(false);
   };

   const fetchData = async () => {
      setLoading(true);
      try {
         const payload = {
            fromDate,
            toDate,
            type: typeFilter,
            button: activeDocumentType,
            companyId,
         };

         if (statusFilter !== 'all') {
            payload.eWayBillStatus = statusFilter;
         }
         await dispatch(getEwayBillandEinvoice(payload));
      } catch (error) {
         console.error('Fetch error:', error);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      if (isEwayBill && companyId) {
         fetchData();
      }
   }, [fromDate, toDate, typeFilter, statusFilter, activeDocumentType, isEwayBill, companyId]);

   useEffect(() => {
      let processedData = [];

      if (Array.isArray(billData)) {
         processedData = billData;
      } else if (billData?.data && Array.isArray(billData.data)) {
         processedData = billData.data;
      } else if (billData?.records && Array.isArray(billData.records)) {
         processedData = billData.records;
      } else if (billData?.result && Array.isArray(billData.result)) {
         processedData = billData.result;
      } else if (billData && typeof billData === 'object') {
         const arrayKeys = Object.keys(billData).filter((key) => Array.isArray(billData[key]));
         if (arrayKeys.length > 0) {
            processedData = billData[arrayKeys[0]];
         }
      }

      const dataWithSelection = processedData.map((item, index) => ({
         ...item,
         selected: false,
         id: item.docno || item.docNumber || item.id || item._id || item.docId || item.salesId,
      }));

      setData(dataWithSelection);
      setSelectAll(false);
   }, [billData]);

   const filteredData = useMemo(() => {
      let filtered = [...data];
      return filtered;
   }, [data, activeTab, typeFilter, currentConfig]);

   const handleSelectItem = (id, checked) => {

      setData((prevData) => {
         return prevData.map((item) => {
            if (item.id === id) {
               return { ...item, selected: checked };
            }
            return item;
         });
      });

      setTimeout(() => {
         const filteredIds = new Set(filteredData.map((item) => item.id));
         const selectedFilteredCount = data.filter((item) => item.selected && filteredIds.has(item.id)).length;
         setSelectAll(selectedFilteredCount === filteredData.length && filteredData.length > 0);
      }, 0);
   };

   const handleSelectAll = (checked) => {
      setSelectAll(checked);

      const filteredIds = new Set(filteredData.map((item) => item.id));

      setData((prevData) => {
         return prevData.map((item) => {
            if (filteredIds.has(item.id)) {
               return { ...item, selected: checked };
            }
            return item;
         });
      });
   };

   const selectedCount = data.filter((item) => item.selected).length;

   const getStatusField = () => {
      return activeDocumentType === 'e-way-bill' ? 'eWayBillStatus' : 'eInvoiceStatus';
   };

   const performBulkActionInternal = async (actionType, selectionType = 'selected') => {
      let itemsToProcess = [];

      if (selectionType === 'selected') {
         itemsToProcess = data.filter((item) => item.selected);
      } else if (selectionType === 'all') {
         if (actionType === 'generate') {
            itemsToProcess = data.filter((item) => {
               const statusField = getStatusField();
               const status =
                  item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';
               return status !== 'generated';
            });
         } else if (actionType === 'cancel') {
            itemsToProcess = data.filter((item) => {
               const statusField = getStatusField();
               const status =
                  item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';
               return status === 'generated' || status === 'pending';
            });
         }
      }

      const salesIds = itemsToProcess
         .map((item) => {
            const id = item.id || item._id || item.salesId || item.docId;
            const numId = Number(id);
            return numId;
         })
         .filter((id) => !isNaN(id) && id > 0);

      if (salesIds.length === 0) {
         console.error('No valid items to process');
         return;
      }

      const payload = {
         salesIds,
         type: typeFilter,
         operationType: currentConfig.operationType,
         action: actionType,
      };

      setActionLoading(true);
      try {
         await dispatch(generateBulkEwayEinvoice(payload));

         const newStatus = actionType === 'generate' ? 'generated' : 'cancelled';
         setData((prevData) =>
            prevData.map((item) => {
               if (itemsToProcess.some((processed) => processed.id === item.id)) {
                  return { ...item, status: newStatus, selected: false };
               }
               return item;
            }),
         );

         setSelectAll(false);
         await fetchData();
      } catch (error) {
         console.error('Bulk action error:', error);
      } finally {
         setActionLoading(false);
      }
   };

   const performBulkAction = async (actionType, selectionType = 'selected') => {
      if (actionType === 'generate') {
         let itemsToCheck = [];

         if (selectionType === 'selected') {
            itemsToCheck = data.filter((item) => item.selected);
         } else if (selectionType === 'all') {
            if (statusFilter === 'all') {
               itemsToCheck = data;
            } else {
               await performBulkActionInternal(actionType, selectionType);
               return;
            }
         }

         const statusField = getStatusField();
         const hasGeneratedItems = itemsToCheck.some((item) => {
            const status = item[statusField]?.toString()?.toLowerCase() || item.status?.toString()?.toLowerCase() || '';
            return status === 'generated';
         });

         if (hasGeneratedItems) {
            setPendingAction({ actionType, selectionType });
            onRegenerateDialogOpen();
            return;
         }
      }

      await performBulkActionInternal(actionType, selectionType);
   };

   const handleRegenerateConfirm = async () => {
      onRegenerateDialogClose();
      if (pendingAction) {
         await performBulkActionInternal(pendingAction.actionType, pendingAction.selectionType);
         setPendingAction(null);
      }
   };

   const handleRegenerateCancel = () => {
      onRegenerateDialogClose();
      setPendingAction(null);
      onClose();
   };

   const handleDocumentTypeChange = (newType) => {
      setActiveDocumentType(newType);
      setActiveTab(0);
      setStatusFilter('pending');
      setTypeFilter(DOCUMENT_CONFIG[newType].tabs[0].type);
      setData([]);
      setSelectAll(false);
      if (isMobile) {
         onFilterClose();
      }
   };

   const MobileActionButtons = () => (
      <VStack spacing={2} w="100%">
         <HStack spacing={2} w="100%">
            <Button
               size="sm"
               variant="outline"
               colorScheme="red"
               onClick={() => performBulkAction('cancel', 'selected')}
               isDisabled={selectedCount === 0 || actionLoading}
               isLoading={actionLoading}
               flex="1">
               Cancel Selected
            </Button>
            <Button
               size="sm"
               variant="outline"
               colorScheme="blue"
               onClick={() => performBulkAction('generate', 'selected')}
               isDisabled={selectedCount === 0 || actionLoading}
               isLoading={actionLoading}
               flex="1">
               Generate Selected
            </Button>
         </HStack>
         <HStack spacing={2} w="100%">
            <Button
               size="sm"
               variant="outline"
               colorScheme="red"
               onClick={() => performBulkAction('cancel', 'all')}
               isDisabled={actionLoading}
               isLoading={actionLoading}
               flex="1">
               Cancel All
            </Button>
            <Button
               size="sm"
               variant="solid"
               colorScheme="blue"
               onClick={() => performBulkAction('generate', 'all')}
               isDisabled={actionLoading}
               isLoading={actionLoading}
               flex="1">
               Generate All
            </Button>
         </HStack>
      </VStack>
   );

   return (
      <>
         <Modal isOpen={isEwayBill} onClose={onClose} size={modalSize} scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent
               maxH="90vh"
               mx={isMobile ? 4 : 'auto'}
               borderRadius="md"
               boxShadow={isDark ? '2xl' : 'xl'}
               bg={isDark ? 'gray.900' : 'white'}
               borderWidth="1px"
               borderColor={isDark ? 'gray.700' : 'gray.200'}>
               {/* Header with Gradient */}
               <ModalHeader
                  pb={4}
                  pt={6}
                  borderBottomWidth="1px"
                  borderColor={isDark ? 'gray.700' : 'gray.200'}
                  bgGradient={isDark ? 'linear(to-r, blue.900, purple.900)' : 'linear(to-r, blue.50, purple.50)'}
                  borderTopRadius="md">
                  <Flex justify="space-between" align="center">
                     <Flex align="center" gap={3}>
                        <Heading
                           size={isMobile ? 'md' : 'lg'}
                           bgGradient={
                              isDark ? 'linear(to-r, blue.300, purple.300)' : 'linear(to-r, blue.600, purple.600)'
                           }
                           bgClip="text"
                           fontWeight="bold">
                           {currentConfig.title} GENERATE
                        </Heading>
                        {(loading || actionLoading) && (
                           <Spinner size="sm" color={isDark ? 'blue.400' : 'blue.600'} thickness="3px" />
                        )}
                     </Flex>

                     {isMobile && (
                        <IconButton
                           icon={<HamburgerIcon />}
                           onClick={onFilterOpen}
                           variant="ghost"
                           colorScheme={isDark ? 'whiteAlpha' : 'gray'}
                           size="sm"
                        />
                     )}
                  </Flex>
               </ModalHeader>

               <ModalCloseButton
                  top={6}
                  right={6}
                  color={isDark ? 'gray.400' : 'gray.500'}
                  _hover={{ bg: isDark ? 'gray.700' : 'gray.100' }}
               />

               {/* Body */}
               <ModalBody p={6}>
                  <Flex direction={isMobile ? 'column' : 'row'} gap={6}>
                     {/* Sidebar - Only on Desktop */}
                     {!isMobile && (
                        <Box
                           w="280px"
                           flexShrink={0}
                           bg={isDark ? 'gray.800' : 'gray.50'}
                           borderRadius="xl"
                           borderWidth="1px"
                           borderColor={isDark ? 'gray.700' : 'gray.200'}
                           p={5}
                           boxShadow="sm">
                           {/* Criteria Section - Enhanced */}
                           <Box
                              mb={6}
                              p={4}
                              borderRadius="lg"
                              bg={isDark ? 'blue.900' : 'blue.50'}
                              borderWidth="2px"
                              borderColor={isDark ? 'blue.700' : 'blue.200'}
                              position="relative"
                              overflow="hidden"
                              _before={{
                                 content: '""',
                                 position: 'absolute',
                                 top: 0,
                                 left: 0,
                                 right: 0,
                                 height: '3px',
                                 bgGradient: isDark
                                    ? 'linear(to-r, blue.400, cyan.400)'
                                    : 'linear(to-r, blue.500, cyan.500)',
                              }}>
                              <Text
                                 fontSize="xs"
                                 fontWeight="bold"
                                 textTransform="uppercase"
                                 letterSpacing="wider"
                                 color={isDark ? 'blue.200' : 'blue.700'}>
                                 📅 Date Criteria
                              </Text>
                           </Box>

                           {/* Date Inputs */}
                           <VStack spacing={4} mb={6}>
                              <FormControl>
                                 <FormLabel
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={isDark ? 'gray.300' : 'gray.700'}
                                    mb={2}>
                                    From Date
                                 </FormLabel>
                                 <Input
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    type="date"
                                    size="sm"
                                    bg={isDark ? 'gray.700' : 'white'}
                                    borderColor={isDark ? 'gray.600' : 'gray.300'}
                                    color={isDark ? 'white' : 'gray.900'}
                                    _hover={{
                                       borderColor: isDark ? 'blue.500' : 'blue.400',
                                    }}
                                    _focus={{
                                       borderColor: isDark ? 'blue.400' : 'blue.500',
                                       boxShadow: isDark
                                          ? '0 0 0 1px var(--chakra-colors-blue-400)'
                                          : '0 0 0 1px var(--chakra-colors-blue-500)',
                                    }}
                                 />
                              </FormControl>

                              <FormControl>
                                 <FormLabel
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={isDark ? 'gray.300' : 'gray.700'}
                                    mb={2}>
                                    To Date
                                 </FormLabel>
                                 <Input
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    type="date"
                                    size="sm"
                                    bg={isDark ? 'gray.700' : 'white'}
                                    borderColor={isDark ? 'gray.600' : 'gray.300'}
                                    color={isDark ? 'white' : 'gray.900'}
                                    _hover={{
                                       borderColor: isDark ? 'blue.500' : 'blue.400',
                                    }}
                                    _focus={{
                                       borderColor: isDark ? 'blue.400' : 'blue.500',
                                       boxShadow: isDark
                                          ? '0 0 0 1px var(--chakra-colors-blue-400)'
                                          : '0 0 0 1px var(--chakra-colors-blue-500)',
                                    }}
                                 />
                              </FormControl>
                           </VStack>

                           {/* Document Types Section - Enhanced */}
                           <Box
                              mb={6}
                              p={4}
                              borderRadius="lg"
                              bg={isDark ? 'green.900' : 'green.50'}
                              borderWidth="2px"
                              borderColor={isDark ? 'green.700' : 'green.200'}
                              position="relative"
                              overflow="hidden"
                              _before={{
                                 content: '""',
                                 position: 'absolute',
                                 top: 0,
                                 left: 0,
                                 right: 0,
                                 height: '3px',
                                 bgGradient: isDark
                                    ? 'linear(to-r, green.400, teal.400)'
                                    : 'linear(to-r, green.500, teal.500)',
                              }}>
                              <Text
                                 fontSize="xs"
                                 fontWeight="bold"
                                 textTransform="uppercase"
                                 letterSpacing="wider"
                                 color={isDark ? 'green.200' : 'green.700'}>
                                 📄 Document Types
                              </Text>
                           </Box>

                           {/* Document Type Buttons */}
                           <VStack spacing={2} mb={6}>
                              {Object.keys(DOCUMENT_CONFIG).map((docType) => (
                                 <Button
                                    key={docType}
                                    onClick={() => handleDocumentTypeChange(docType)}
                                    w="100%"
                                    size="sm"
                                    fontWeight="medium"
                                    bg={
                                       activeDocumentType === docType
                                          ? isDark
                                             ? 'blue.600'
                                             : 'blue.500'
                                          : isDark
                                             ? 'gray.700'
                                             : 'white'
                                    }
                                    color={activeDocumentType === docType ? 'white' : isDark ? 'gray.300' : 'gray.700'}
                                    borderWidth="1px"
                                    borderColor={
                                       activeDocumentType === docType
                                          ? isDark
                                             ? 'blue.500'
                                             : 'blue.400'
                                          : isDark
                                             ? 'gray.600'
                                             : 'gray.300'
                                    }
                                    _hover={{
                                       transform: 'translateY(-1px)',
                                       boxShadow: 'md',
                                       bg:
                                          activeDocumentType === docType
                                             ? isDark
                                                ? 'blue.500'
                                                : 'blue.600'
                                             : isDark
                                                ? 'gray.600'
                                                : 'gray.100',
                                    }}
                                    transition="all 0.2s">
                                    {DOCUMENT_CONFIG[docType].title}s
                                 </Button>
                              ))}
                           </VStack>

                           {/* Filters Section - Enhanced */}
                           <Box
                              p={4}
                              borderRadius="lg"
                              bg={isDark ? 'orange.900' : 'orange.50'}
                              borderWidth="2px"
                              borderColor={isDark ? 'orange.700' : 'orange.200'}
                              position="relative"
                              overflow="hidden"
                              _before={{
                                 content: '""',
                                 position: 'absolute',
                                 top: 0,
                                 left: 0,
                                 right: 0,
                                 height: '3px',
                                 bgGradient: isDark
                                    ? 'linear(to-r, orange.400, red.400)'
                                    : 'linear(to-r, orange.500, red.500)',
                              }}>
                              <Text
                                 fontSize="xs"
                                 fontWeight="bold"
                                 textTransform="uppercase"
                                 letterSpacing="wider"
                                 color={isDark ? 'orange.200' : 'orange.700'}
                                 mb={4}>
                                 🔍 Status Filter
                              </Text>

                              <FormControl>
                                 <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    size="sm"
                                    bg={isDark ? 'gray.700' : 'white'}
                                    borderColor={isDark ? 'gray.600' : 'gray.300'}
                                    color={isDark ? 'white' : 'gray.900'}
                                    _hover={{
                                       borderColor: isDark ? 'orange.500' : 'orange.400',
                                    }}
                                    _focus={{
                                       borderColor: isDark ? 'orange.400' : 'orange.500',
                                       boxShadow: isDark
                                          ? '0 0 0 1px var(--chakra-colors-orange-400)'
                                          : '0 0 0 1px var(--chakra-colors-orange-500)',
                                    }}>
                                    {STATUS_OPTIONS.map((option) => (
                                       <option key={option.value} value={option.value}>
                                          {option.label}
                                       </option>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Box>
                        </Box>
                     )}

                     {/* Main Content */}
                     <Box
                        flex="1"
                        bg={isDark ? 'gray.800' : 'white'}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={isDark ? 'gray.700' : 'gray.200'}
                        overflow="hidden"
                        boxShadow="sm">
                        {/* Tabs */}
                        <Box
                           borderBottomWidth="1px"
                           borderColor={isDark ? 'gray.700' : 'gray.200'}
                           bg={isDark ? 'gray.750' : 'gray.50'}>
                           <Flex>
                              {currentConfig.tabs.map((tab, index) => (
                                 <Button
                                    key={index}
                                    onClick={() => handleTabChange(index)}
                                    flex={isMobile ? '1' : 'initial'}
                                    px={isMobile ? 2 : 6}
                                    py={3}
                                    borderRadius="0"
                                    size="sm"
                                    fontWeight="medium"
                                    bg={activeTab === index ? (isDark ? 'gray.800' : 'white') : 'transparent'}
                                    color={
                                       activeTab === index
                                          ? isDark
                                             ? 'blue.300'
                                             : 'blue.600'
                                          : isDark
                                             ? 'gray.400'
                                             : 'gray.600'
                                    }
                                    borderBottomWidth="2px"
                                    borderBottomColor={
                                       activeTab === index ? (isDark ? 'blue.400' : 'blue.500') : 'transparent'
                                    }
                                    _hover={{
                                       bg:
                                          activeTab === index
                                             ? isDark
                                                ? 'gray.800'
                                                : 'white'
                                             : isDark
                                                ? 'gray.700'
                                                : 'gray.100',
                                       color:
                                          activeTab === index
                                             ? isDark
                                                ? 'blue.300'
                                                : 'blue.600'
                                             : isDark
                                                ? 'gray.300'
                                                : 'gray.800',
                                    }}
                                    transition="all 0.2s">
                                    {isMobile ? tab.label.split(' ')[0] : tab.label}
                                 </Button>
                              ))}
                           </Flex>
                        </Box>

                        {/* Table Content */}
                        {currentConfig.tabs.map((_, index) => (
                           <Box key={index} display={activeTab === index ? 'block' : 'none'}>
                              <ResponsiveDataDisplay
                                 columns={currentConfig.columns.main}
                                 data={filteredData}
                                 loading={loading}
                                 textColor={textColor}
                                 colorMode={colorMode}
                                 tableHeaderBg={tableHeaderBg}
                                 tableHoverBg={tableHoverBg}
                                 selectAll={selectAll}
                                 onSelectAll={handleSelectAll}
                                 onSelectItem={handleSelectItem}
                                 activeDocumentType={activeDocumentType}
                                 isMobile={isMobile}
                              />
                           </Box>
                        ))}
                     </Box>
                  </Flex>
               </ModalBody>

               {/* Footer */}
               <ModalFooter
                  borderTopWidth="1px"
                  borderColor={isDark ? 'gray.700' : 'gray.200'}
                  bg={isDark ? 'gray.800' : 'gray.50'}
                  flexDirection="column"
                  borderBottomRadius={'md'}
                  gap={4}
                  p={6}>
                  {/* Info Badge */}
                  <Flex
                     w="100%"
                     justify="center"
                     align="center"
                     gap={3}
                     flexWrap="wrap"
                     fontSize="sm"
                     color={isDark ? 'gray.400' : 'gray.600'}>
                     <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="xs">
                        {filteredData.length} Records
                     </Badge>
                     <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="xs">
                        {selectedCount} Selected
                     </Badge>
                     <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="xs">
                        Type: {typeFilter}
                     </Badge>
                     <Badge colorScheme="orange" px={3} py={1} borderRadius="full" fontSize="xs">
                        Status: {statusFilter}
                     </Badge>
                  </Flex>

                  {/* Action Buttons */}
                  {isMobile ? (
                     <MobileActionButtons />
                  ) : (
                     <Flex w="100%" justify="space-between" align="center" gap={3}>
                        <Box></Box>
                        <Flex gap={2} flexWrap="wrap">
                           <Button
                              onClick={() => performBulkAction('cancel', 'selected')}
                              isDisabled={selectedCount === 0 || actionLoading}
                              isLoading={actionLoading}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              fontWeight="medium">
                              Cancel Selected
                           </Button>

                           <Button
                              onClick={() => performBulkAction('cancel', 'all')}
                              isDisabled={actionLoading}
                              isLoading={actionLoading}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              fontWeight="medium">
                              Cancel All
                           </Button>

                           <Button
                              onClick={() => performBulkAction('generate', 'selected')}
                              isDisabled={selectedCount === 0 || actionLoading}
                              isLoading={actionLoading}
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              fontWeight="medium">
                              Generate Selected
                           </Button>

                           <Button
                              onClick={() => performBulkAction('generate', 'all')}
                              isDisabled={actionLoading}
                              isLoading={actionLoading}
                              size="sm"
                              bgGradient={
                                 isDark ? 'linear(to-r, blue.500, purple.600)' : 'linear(to-r, blue.500, purple.600)'
                              }
                              color="white"
                              fontWeight="medium"
                              _hover={{
                                 bgGradient: isDark
                                    ? 'linear(to-r, blue.600, purple.700)'
                                    : 'linear(to-r, blue.600, purple.700)',
                                 transform: 'translateY(-1px)',
                                 boxShadow: 'lg',
                              }}
                              _active={{
                                 transform: 'translateY(0)',
                              }}
                              transition="all 0.2s">
                              Generate All
                           </Button>

                           <Button
                              onClick={onClose}
                              isDisabled={actionLoading}
                              size="sm"
                              variant="ghost"
                              colorScheme={isDark ? 'whiteAlpha' : 'gray'}
                              fontWeight="medium">
                              Close
                           </Button>
                        </Flex>
                     </Flex>
                  )}
               </ModalFooter>
            </ModalContent>
         </Modal>

         {/* Mobile Filter Panel */}
         <MobileFilterPanel
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            activeDocumentType={activeDocumentType}
            handleDocumentTypeChange={handleDocumentTypeChange}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            textColor={textColor}
            inputBg={inputBg}
         />

         {/* Regenerate Confirmation Dialog */}
         <AlertDialog isOpen={isRegenerateDialogOpen} leastDestructiveRef={cancelRef} onClose={onRegenerateDialogClose}>
            <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(4px)">
               <AlertDialogContent mx={isMobile ? 4 : 0}>
                  <AlertDialogHeader fontSize="lg" fontWeight="bold">
                     Regenerate {currentConfig.title}
                  </AlertDialogHeader>

                  <AlertDialogBody>
                     Some items are already generated. Do you want to regenerate them?
                     <Text mt={2} fontSize="sm" color="gray.600">
                        This will regenerate all items including those that are already generated.
                     </Text>
                  </AlertDialogBody>

                  <AlertDialogFooter>
                     <Button ref={cancelRef} onClick={handleRegenerateCancel}>
                        No
                     </Button>
                     <Button colorScheme="blue" onClick={handleRegenerateConfirm} ml={3}>
                        Yes, Regenerate
                     </Button>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialogOverlay>
         </AlertDialog>
      </>
   );
}
